import { Link } from 'react-router-dom';
import SeoHead from '../components/SeoHead';

export default function Home() {
  return (
    <div className="article-container text-[#221E1B]">
      <SeoHead
        title="Radiatorproblemer.dk – Uafhængig guide til radiatorer"
        description="Praktisk og uafhængig referenceguide til radiatorproblemer: radiatoren varmer ikke, er kold i bunden, klikker, larmer eller skal luftes ud."
        canonicalPath="/"
      />

      <header className="mb-10">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#221E1B] leading-tight mb-4">
          Hvad er der galt med din radiator?
        </h1>
        <p className="text-[17px] leading-[1.65] text-[#221E1B]">
          Dette er en uafhængig dansk opslagsguide til de typiske problemer, der opstår i vandbårne og elektriske varmeanlæg.
        </p>
        <p className="text-[17px] leading-[1.65] text-[#6B625B] mt-2">
          Find det direkte svar på dit symptom herunder. Hver vejledning gennemgår først, hvad du selv kan afprøve trin for trin, og hvornår det er nødvendigt at tilkalde en autoriseret fagmand.
        </p>
      </header>

      {/* Hurtiggenveje til mest søgte problemer (1, 8, 12) */}
      <div className="bg-[#F3E6DC] border border-[#E6DED5] rounded p-5 mb-12">
        <div className="text-xs uppercase tracking-wider font-bold text-[#B5531E] mb-3">
          Mest søgte vejledninger
        </div>
        <ul className="space-y-2 text-[17px]">
          <li>
            <Link
              to="/radiator-varmer-ikke"
              className="text-[#221E1B] font-semibold hover:text-[#B5531E] hover:underline underline-offset-4"
            >
              1. Radiatoren varmer ikke
            </Link>
            <span className="text-sm text-[#6B625B] block sm:inline sm:ml-2">
              – tjeklisten fra det simple til fagmanden
            </span>
          </li>
          <li>
            <Link
              to="/radiator-klikker"
              className="text-[#221E1B] font-semibold hover:text-[#B5531E] hover:underline underline-offset-4"
            >
              2. Radiatoren klikker eller tikker
            </Link>
            <span className="text-sm text-[#6B625B] block sm:inline sm:ml-2">
              – varmeudvidelse og rør i klemme
            </span>
          </li>
          <li>
            <Link
              to="/udluftning-af-radiator"
              className="text-[#221E1B] font-semibold hover:text-[#B5531E] hover:underline underline-offset-4"
            >
              3. Sådan lufter du en radiator ud
            </Link>
            <span className="text-sm text-[#6B625B] block sm:inline sm:ml-2">
              – trinvis guide, rækkefølge og vandtryk
            </span>
          </li>
        </ul>
      </div>

      {/* De tre emneområder */}
      <div className="space-y-12">
        {/* Radiatoren varmer ikke */}
        <section>
          <h2 className="text-xl font-bold text-[#221E1B] mb-2 tracking-tight">
            Radiatoren varmer ikke
          </h2>
          <p className="text-sm text-[#6B625B] mb-4">
            Når hele eller dele af radiatoren forbliver kold, eller varmen fordeler sig uensartet.
          </p>
          <ul className="divide-y divide-[#E6DED5] border-y border-[#E6DED5] text-[17px]">
            <li className="py-3">
              <Link
                to="/radiator-varmer-ikke"
                className="text-[#B5531E] hover:underline font-medium"
              >
                Radiatoren varmer ikke
              </Link>
              <p className="text-sm text-[#6B625B] mt-0.5">
                Komplet fejlfindingsliste fra ventilstift til anlægstryk.
              </p>
            </li>
            <li className="py-3">
              <Link
                to="/radiator-kold-i-bunden"
                className="text-[#B5531E] hover:underline font-medium"
              >
                Radiator kold i bunden og varm i toppen
              </Link>
              <p className="text-sm text-[#6B625B] mt-0.5">
                Hvornår det er helt normalt (god afkøling), og hvornår det er en fejl.
              </p>
            </li>
            <li className="py-3">
              <Link
                to="/radiator-varmer-ikke-efter-udluftning"
                className="text-[#B5531E] hover:underline font-medium"
              >
                Radiator varmer ikke efter udluftning
              </Link>
              <p className="text-sm text-[#6B625B] mt-0.5">
                Luften er ude, men radiatoren er stadig kold: de næste tre ting at tjekke.
              </p>
            </li>
            <li className="py-3">
              <Link
                to="/fjernvarme-radiator-virker-ikke"
                className="text-[#B5531E] hover:underline font-medium"
              >
                Fjernvarme og radiatorer
              </Link>
              <p className="text-sm text-[#6B625B] mt-0.5">
                Afkøling, returtemperatur og hvorfor kold bund sparer dig for afkølingstarif.
              </p>
            </li>
            <li className="py-3">
              <Link
                to="/el-radiator-varmer-ikke"
                className="text-[#B5531E] hover:underline font-medium"
              >
                El-radiatoren varmer ikke
              </Link>
              <p className="text-sm text-[#6B625B] mt-0.5">
                Termostat, sikring og termosikring – og hvornår den er udtjent.
              </p>
            </li>
            <li className="py-3">
              <Link
                to="/gammel-radiator-varmer-ikke"
                className="text-[#B5531E] hover:underline font-medium"
              >
                Gammel radiator varmer ikke
              </Link>
              <p className="text-sm text-[#6B625B] mt-0.5">
                Støbejern, slam og magnetit: tilstoppet, rusten eller fejlindstillet?
              </p>
            </li>
            <li className="py-3">
              <Link
                to="/radiator-og-varmepumpe"
                className="text-[#B5531E] hover:underline font-medium"
              >
                Radiatorer med varmepumpe
              </Link>
              <p className="text-sm text-[#6B625B] mt-0.5">
                Hvorfor radiatorerne føles lunkne ved lav fremløbstemperatur.
              </p>
            </li>
          </ul>
        </section>

        {/* Lyde fra radiatoren */}
        <section>
          <h2 className="text-xl font-bold text-[#221E1B] mb-2 tracking-tight">
            Lyde fra radiatoren
          </h2>
          <p className="text-sm text-[#6B625B] mb-4">
            Når anlægget klukker, suser, klikker eller banker.
          </p>
          <ul className="divide-y divide-[#E6DED5] border-y border-[#E6DED5] text-[17px]">
            <li className="py-3">
              <Link
                to="/radiator-klikker"
                className="text-[#B5531E] hover:underline font-medium"
              >
                Radiatoren klikker
              </Link>
              <p className="text-sm text-[#6B625B] mt-0.5">
                Varmeudvidelse i metallet, rør i gennemføringer og klik om natten.
              </p>
            </li>
            <li className="py-3">
              <Link
                to="/radiator-larmer"
                className="text-[#B5531E] hover:underline font-medium"
              >
                Radiatoren larmer, suser eller banker
              </Link>
              <p className="text-sm text-[#6B625B] mt-0.5">
                Luft, for højt pumpetryk eller modsat ventilflow.
              </p>
            </li>
            <li className="py-3">
              <Link
                to="/radiator-lyder-som-vand"
                className="text-[#B5531E] hover:underline font-medium"
              >
                Det lyder som rislende vand eller et vandfald
              </Link>
              <p className="text-sm text-[#6B625B] mt-0.5">
                Luftbobler i lamellerne og hvordan de udluftes systematisk.
              </p>
            </li>
            <li className="py-3">
              <Link
                to="/radiator-banker-fjernvarme"
                className="text-[#B5531E] hover:underline font-medium"
              >
                Banken og slag i fjernvarmeanlægget
              </Link>
              <p className="text-sm text-[#6B625B] mt-0.5">
                Trykstød, differenstryk og hvornår fagmanden skal tilkaldes.
              </p>
            </li>
          </ul>
        </section>

        {/* Udluftning, termostat og tryk */}
        <section>
          <h2 className="text-xl font-bold text-[#221E1B] mb-2 tracking-tight">
            Udluftning, termostat og tryk
          </h2>
          <p className="text-sm text-[#6B625B] mb-4">
            Vejledninger til drift, regulering, vedligeholdelse og priser.
          </p>
          <ul className="divide-y divide-[#E6DED5] border-y border-[#E6DED5] text-[17px]">
            <li className="py-3">
              <Link
                to="/udluftning-af-radiator"
                className="text-[#B5531E] hover:underline font-medium"
              >
                Sådan lufter du en radiator ud
              </Link>
              <p className="text-sm text-[#6B625B] mt-0.5">
                Nøgle, korrekt rækkefølge fra bund til top og pumpeafbrydelse.
              </p>
            </li>
            <li className="py-3">
              <Link
                to="/radiator-luftskrue-sidder-fast"
                className="text-[#B5531E] hover:underline font-medium"
              >
                Luftskruen sidder fast eller mangler
              </Link>
              <p className="text-sm text-[#6B625B] mt-0.5">
                Hvad man må prøve forsigtigt, og hvad man absolut skal undgå.
              </p>
            </li>
            <li className="py-3">
              <Link
                to="/radiatornoegle"
                className="text-[#B5531E] hover:underline font-medium"
              >
                Radiatornøgle: Hvilken nøgle passer?
              </Link>
              <p className="text-sm text-[#6B625B] mt-0.5">
                Typer af luftskruer, det rigtige værktøj og hvad du gør uden nøgle.
              </p>
            </li>
            <li className="py-3">
              <Link
                to="/radiator-termostat"
                className="text-[#B5531E] hover:underline font-medium"
              >
                Radiatortermostater forklaret
              </Link>
              <p className="text-sm text-[#6B625B] mt-0.5">
                Mekaniske følere, fjernfølere, wi-fi og tegn på slidte bælge.
              </p>
            </li>
            <li className="py-3">
              <Link
                to="/radiator-termostat-indstilling"
                className="text-[#B5531E] hover:underline font-medium"
              >
                Hvad betyder tallene på termostaten i grader?
              </Link>
              <p className="text-sm text-[#6B625B] mt-0.5">
                Oversættelse af Danfoss-skalaen til konkrete rumtemperaturer.
              </p>
            </li>
            <li className="py-3">
              <Link
                to="/radiator-ventil-sidder-fast"
                className="text-[#B5531E] hover:underline font-medium"
              >
                Ventilstiften sidder fast efter sommeren
              </Link>
              <p className="text-sm text-[#6B625B] mt-0.5">
                Sådan masseres den 2 mm stålstift fri uden at trække den ud.
              </p>
            </li>
            <li className="py-3">
              <Link
                to="/radiator-varmer-selvom-den-er-slukket"
                className="text-[#B5531E] hover:underline font-medium"
              >
                Radiatoren varmer, selvom den er slukket
              </Link>
              <p className="text-sm text-[#6B625B] mt-0.5">
                Løst termostathoved, ventil der ikke lukker, eller varme rør.
              </p>
            </li>
            <li className="py-3">
              <Link
                to="/radiator-returventil"
                className="text-[#B5531E] hover:underline font-medium"
              >
                Returventil og forindstilling
              </Link>
              <p className="text-sm text-[#6B625B] mt-0.5">
                Hydraulisk balance, forindstillingsringe og strengregulering.
              </p>
            </li>
            <li className="py-3">
              <Link
                to="/radiator-laekker"
                className="text-[#B5531E] hover:underline font-medium"
              >
                Radiatoren lækker vand
              </Link>
              <p className="text-sm text-[#6B625B] mt-0.5">
                Akut skadesbegrænsning, afspærring og udskiftning af pakdåse.
              </p>
            </li>
            <li className="py-3">
              <Link
                to="/radiator-tryk"
                className="text-[#B5531E] hover:underline font-medium"
              >
                Trykket i varmeanlægget er faldet
              </Link>
              <p className="text-sm text-[#6B625B] mt-0.5">
                Hvad manometeret skal vise i bar, og hvorfor trykket dykker.
              </p>
            </li>
            <li className="py-3">
              <Link
                to="/hvordan-virker-en-radiator"
                className="text-[#B5531E] hover:underline font-medium"
              >
                Hvordan virker en radiator, termostat og måler?
              </Link>
              <p className="text-sm text-[#6B625B] mt-0.5">
                Konvektion, strålevarme og 2-føler elektroniske varmemålere.
              </p>
            </li>
            <li className="py-3">
              <Link
                to="/spar-paa-varmen"
                className="text-[#B5531E] hover:underline font-medium"
              >
                Sådan sparer du på varmen
              </Link>
              <p className="text-sm text-[#6B625B] mt-0.5">
                De 6 officielle spareråd: 20 cm afstand, ens trin og udluftning.
              </p>
            </li>
            <li className="py-3">
              <Link
                to="/radiator-udskiftning-pris"
                className="text-[#B5531E] hover:underline font-medium"
              >
                Hvad koster udskiftning af radiator eller ventil?
              </Link>
              <p className="text-sm text-[#6B625B] mt-0.5">
                Realistiske prisspænd for materialer og autoriseret VVS-arbejde.
              </p>
            </li>
          </ul>
        </section>
      </div>

      <div className="mt-12 pt-8 border-t border-[#E6DED5] flex items-center justify-between text-sm text-[#6B625B]">
        <Link to="/ordbog" className="text-[#B5531E] hover:underline font-medium">
          → Gå til VVS-ordbogen med fagudtryk
        </Link>
        <Link to="/om" className="hover:underline">
          Om Radiatorproblemer.dk
        </Link>
      </div>
    </div>
  );
}
